import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const activeSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    const currentCat = categories.find(c => c.slug === activeSlug);
    const catName = currentCat ? currentCat.name : '全部';
    document.title = `${catName}韩漫 - 基德漫画网免费在线观看`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `基德漫画网${catName}韩漫分类，提供${catName}韩漫免费漫画在线观看下拉式，海量${catName}漫画每日更新，高清画质免费阅读。`);
  }, [activeSlug]);

  const filteredComics = activeSlug === 'all'
    ? comics
    : comics.filter(c => c.categories.includes(activeSlug));

  const sortedComics = [...filteredComics].sort((a, b) => {
    if (sortBy === 'latest') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    if (sortBy === 'popular') return b.views - a.views;
    return b.rating - a.rating;
  });

  const currentCat = categories.find(c => c.slug === activeSlug);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: `${currentCat?.name || '全部'}韩漫 - 基德漫画网`,
              description: currentCat?.description || '浏览所有韩漫作品',
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.jidemanhua.com/' },
                  { '@type': 'ListItem', position: 2, name: currentCat?.name || '全部漫画' },
                ],
              },
            }),
          }}
        />

        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: currentCat?.name || '全部漫画' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1280px] mx-auto">
            <div className="mb-6">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {currentCat?.name || '全部'}韩漫
              </h1>
              <p className="text-sm text-foreground-500 mt-2">{currentCat?.description || '浏览所有韩漫作品'}</p>
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-6">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={`/category/${cat.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    window.REACT_APP_NAVIGATE(`/category/${cat.slug}`);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'
                  }`}
                >
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{sortedComics.length}</strong> 部漫画
              </p>
              <div className="flex items-center gap-1 bg-secondary-100 rounded-full p-1">
                {[
                  { key: 'latest' as const, label: '最新' },
                  { key: 'popular' as const, label: '人气' },
                  { key: 'rating' as const, label: '评分' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      sortBy === opt.key
                        ? 'bg-background-50 text-foreground-950'
                        : 'text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {sortedComics.map((comic) => (
                <ComicCard
                  key={comic.id}
                  id={comic.id}
                  title={comic.title}
                  author={comic.author}
                  cover={comic.cover}
                  rating={comic.rating}
                  status={comic.status}
                  categorySlug={activeSlug}
                />
              ))}
            </div>

            {sortedComics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-foreground-400 mb-4">
                  <i className="ri-emotion-sad-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
                <a
                  href="/category/all"
                  onClick={(e) => {
                    e.preventDefault();
                    window.REACT_APP_NAVIGATE('/category/all');
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-1 mt-4 text-sm text-primary-600 font-medium cursor-pointer"
                >
                  查看全部漫画 <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}